import React from "react";
import BaseController from "../../common/base/BaseController";
import { showToast } from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import routes from "../../navigation/routes";
import { navigationRef } from "../../../App";
import { onboardingSteps } from "../data/onboardingSteps";
import NtOnboardingLayout from "../components/NtOnboardingLayout";
import NtOnboardingTopRail from "../components/NtOnboardingTopRail";
import NtOnboardingStepper from "../components/NtOnboardingStepper";
import NtOnboardingHeader from "../components/NtOnboardingHeader";
import NtOnboardingFooter from "../components/NtOnboardingFooter";
import NtOnboardingWelcomeStep from "../components/steps/NtOnboardingWelcomeStep";
import NtOnboardingCompanyStep from "../components/steps/NtOnboardingCompanyStep";
import NtOnboardingAdminStep from "../components/steps/NtOnboardingAdminStep";
import NtOnboardingReviewStep from "../components/steps/NtOnboardingReviewStep";
import NtOnboardingVerifyStep from "../components/steps/NtOnboardingVerifyStep";
// Kept around for when billing comes back online — the step components are
// preserved so re-enabling is a one-line change in onboardingSteps.js. Lint
// may flag the unused imports; that's intentional.
// eslint-disable-next-line no-unused-vars
import NtOnboardingPlanStep from "../components/steps/NtOnboardingPlanStep";
// eslint-disable-next-line no-unused-vars
import NtOnboardingPaymentStep from "../components/steps/NtOnboardingPaymentStep";
import { getAuthService, getOnboardingService } from "@ntconnect/framework";
import { fetchPermissions } from "../../api/rbac";
import {
  isValidAlias,
  isValidEmail,
  isValidPassword,
} from "../utils/validationUtils";

const STEP_COPY = {
  welcome: {
    title: "Welcome to Voxcraft.",
    subtitle:
      "Let's set up your PBX. We'll walk you through a few quick steps — you can review everything before anything is created.",
  },
  company: {
    title: "Tell us about your company",
    subtitle:
      "This becomes your company alias once your account is created.",
  },
  admin: {
    title: "Create your admin user",
    subtitle:
      "This is the account you'll sign in with after signup. Save the credentials somewhere safe.",
  },
  review: {
    title: "Review and confirm",
    subtitle:
      "Double-check everything below. On the next step we'll email a verification code and finish creating your account.",
  },
  verify: {
    title: "Verify your email",
    subtitle:
      "We just sent a 6-digit code to the admin email. Enter it to finish creating your account.",
  },
};

// Resend cooldown — matches the BFF's per-email rate limit (1/min). We add a
// small buffer so the button is back live the moment the server is willing.
const RESEND_COOLDOWN_MS = 60_000;

class OnboardingController extends BaseController {
  state = {
    ...super.state,
    currentStep: 0,
    loading: false,
    company: { name: "", domain_name: "", description: "" },
    admin: { name: "", email: "", password: "", passwordConfirm: "" },
    // Inline alias-availability error surfaced on the company step's alias
    // field when the chosen alias is taken/reserved/invalid.
    aliasError: null,
    // Verify-step state. `codeError` lets us surface the BFF's
    // "invalid_security_code" / "security_code_expired" body inline without
    // bouncing the whole wizard back to the company step.
    securityCode: "",
    codeSending: false,
    codeError: null,
    codeRequestedFor: null, // admin.email the current code was sent to
    codeResendAt: null,     // epoch ms; resend is gated until this passes
  };

  componentDidMount() {
    super.componentDidMount();
    document.title = "Voxcraft | Get Started";
  }

  // When the user lands on the verify step (either from a forward step or by
  // jumping back to it via the stepper), kick off a code request if we haven't
  // already sent one to this email address.
  componentDidUpdate(prevProps, prevState) {
    const stepKey = (i) => onboardingSteps[i]?.key;
    const nowOnVerify = stepKey(this.state.currentStep) === "verify";
    const wasOnVerify = stepKey(prevState.currentStep) === "verify";
    const justArrived = nowOnVerify && !wasOnVerify;
    const emailChanged = this.state.codeRequestedFor && this.state.codeRequestedFor !== this.state.admin.email;
    if (nowOnVerify && (justArrived || emailChanged) && this.state.admin.email && !this.state.codeSending) {
      this.requestSecurityCode();
    }
  }

  isStepValid = () => {
    const { currentStep, company, admin, securityCode } = this.state;
    const key = onboardingSteps[currentStep]?.key;
    if (key === "company") {
      return !!(
        company.name &&
        isValidAlias(company.domain_name) &&
        company.description
      );
    }
    if (key === "admin") {
      return !!(
        admin.name &&
        isValidEmail(admin.email) &&
        isValidPassword(admin.password) &&
        admin.password === admin.passwordConfirm
      );
    }
    if (key === "verify") {
      return /^\d{6}$/.test((securityCode || "").trim());
    }
    return true;
  };

  handleSelectStep = (index) => {
    if (!this.state.loading) {
      this.setState({ currentStep: index });
    }
  };

  handleEditStep = (key) => {
    const index = onboardingSteps.findIndex((s) => s.key === key);
    if (index >= 0) this.handleSelectStep(index);
  };

  handleBack = () => {
    if (this.state.loading) return;
    this.setState((s) => ({ currentStep: Math.max(0, s.currentStep - 1) }));
  };

  handleSkip = () => this.runStep(true);
  handleContinue = () => this.runStep(false);

  // User is pre-auth at this point, so exiting bails back to the marketing
  // home page. Matches the exit affordance in the setup wizard.
  handleExit = () => {
    if (this.state.loading) return;
    if (navigationRef.current) {
      navigationRef.current.navigate(routes.marketing);
    }
  };

  runStep = async (skipping) => {
    if (this.state.loading) return;
    if (!skipping && !this.isStepValid()) {
      showToast(
        "Heads up",
        "Please fill the required fields before continuing.",
        "info"
      );
      return;
    }

    const key = onboardingSteps[this.state.currentStep].key;
    this.setState({ loading: true });
    try {
      // Company step: confirm the alias is free before advancing so a taken
      // alias is caught here, not all the way at final signup.
      if (!skipping && key === "company") {
        const available = await this.checkAliasAvailable(
          this.state.company.domain_name
        );
        if (!available) return; // aliasError is set; finally clears loading
      }
      await this.advance();
    } catch (e) {
      Logger("Onboarding step failed", key, e);
      showToast("Something went wrong", e?.message || "Please try again.", "error");
    } finally {
      this.setState({ loading: false });
    }
  };

  // Ask the BFF whether the alias/subdomain is free. Returns true when the
  // step may advance. On a definitive "not available" we set an inline
  // aliasError and block; on a network/unknown error we let the user proceed
  // (signup validates the alias server-side as the backstop) rather than trap
  // them behind a flaky check.
  checkAliasAvailable = async (domain) => {
    this.setState({ aliasError: null });
    try {
      const res = await getOnboardingService().availability(domain);
      Logger("Onboarding · availability", res);
      if (res?.available) return true;
      this.setState({ aliasError: aliasReasonMessage(res?.reason) });
      return false;
    } catch (e) {
      Logger("Onboarding · availability check failed", e);
      return true;
    }
  };

  advance = async () => {
    const { currentStep } = this.state;
    if (currentStep === onboardingSteps.length - 1) {
      await this.handleFinish();
      return;
    }
    this.setState({ currentStep: currentStep + 1 });
  };

  /**
   * POST /onboarding/security-code/request → sends a 6-digit code to the
   * admin email. We track the cooldown locally so the Resend button is
   * disabled for 60s after each send, matching the BFF's 1/min/email rate
   * limit.
   */
  requestSecurityCode = async () => {
    const { admin } = this.state;
    if (!isValidEmail(admin.email)) return;
    this.setState({ codeSending: true, codeError: null });
    try {
      const res = await getOnboardingService().requestSecurityCode({ email: admin.email });
      Logger("Onboarding · requestSecurityCode", res);
      this.setState({
        codeSending: false,
        codeRequestedFor: admin.email,
        codeResendAt: Date.now() + RESEND_COOLDOWN_MS,
      });
    } catch (e) {
      Logger("Onboarding · requestSecurityCode failed", e);
      this.setState({ codeSending: false, codeError: e?.message || "Couldn't send code. Try again." });
    }
  };

  // The verify step's Continue button lands here. This is the single place
  // where we actually mutate anything on the backend: POST /onboarding/signup
  // creates the FusionPBX domain + admin user, persists the BFF tenant row,
  // and returns a fresh access/refresh token pair — the OnboardingService
  // already persists those in TokenStorage so the next PBX call is
  // automatically authenticated.
  //
  // TODO(plan): once the plan API is online, decide here whether to drop the
  // user straight into the dashboard or show the plan-picker first. For now
  // we always sign them in directly.
  handleFinish = async () => {
    const { company, admin, securityCode } = this.state;

    const signup = await getOnboardingService().signup({
      business: {
        domain: company.domain_name,
        displayName: company.name,
      },
      admin: {
        username: admin.email,
        password: admin.password,
        email: admin.email,
        fullName: admin.name,
      },
      securityCode,
    });
    Logger("Onboarding · signup", signup);

    // Build the BusinessUser shape the rest of the app expects. Pull
    // userId/tenantId/role from the JWT claims (no extra round-trip), and
    // fall back to the signup response for tenant metadata.
    const claims = decodeJwtClaims(signup.tokens.accessToken);
    const domainSlug = company.domain_name;
    const businessUser = {
      schema: "business",
      userId: String(claims.sub || claims.userId || ""),
      username: admin.email,
      displayName: admin.name,
      accountId: admin.email,
      token: signup.tokens.accessToken,
      domain: domainSlug,
      tenant: signup.tenant?.id || String(claims.tenantId || ""),
      email: admin.email,
      role: String(claims.role || ""),
    };

    const permissions = await fetchPermissions(
      domainSlug,
      businessUser.userId,
      1,
      1000000
    );

    this.context.updateUser({
      ...this.context.user,
      ...businessUser,
      authenticated: true,
      permissions: permissions.ok ? permissions.data?.data : [],
    });

    showToast(
      "Welcome to Voxcraft",
      "Your account is ready. Signing you in now.",
      "success"
    );
    // The navigator switches automatically once `authenticated: true` flips,
    // dropping us onto the dashboard. No explicit navigate needed.
  };

  renderStep = () => {
    const key = onboardingSteps[this.state.currentStep]?.key;
    if (key === "welcome") return <NtOnboardingWelcomeStep />;
    if (key === "company") {
      return (
        <NtOnboardingCompanyStep
          company={this.state.company}
          aliasError={this.state.aliasError}
          onChangeCompany={(company) =>
            this.setState({ company, aliasError: null })
          }
        />
      );
    }
    if (key === "admin") {
      return (
        <NtOnboardingAdminStep
          admin={this.state.admin}
          onChangeAdmin={(admin) => this.setState({ admin })}
        />
      );
    }
    if (key === "review") {
      return (
        <NtOnboardingReviewStep
          company={this.state.company}
          admin={this.state.admin}
          onEditStep={this.handleEditStep}
        />
      );
    }
    if (key === "verify") {
      return (
        <NtOnboardingVerifyStep
          email={this.state.admin.email}
          code={this.state.securityCode}
          onChangeCode={(securityCode) => this.setState({ securityCode, codeError: null })}
          sending={this.state.codeSending}
          resendDisabledUntil={this.state.codeResendAt}
          onResend={this.requestSecurityCode}
          lastError={this.state.codeError}
        />
      );
    }
    return null;
  };

  render() {
    const { currentStep, loading } = this.state;
    const stepKey = onboardingSteps[currentStep]?.key;
    const copy = STEP_COPY[stepKey] || { title: "", subtitle: "" };
    const finishLabel = stepKey === "verify" ? "Create my account" : "Complete signup";
    return (
      <NtOnboardingLayout
        top={
          <>
            <NtOnboardingTopRail steps={onboardingSteps} currentStep={currentStep} />
            <NtOnboardingStepper
              steps={onboardingSteps}
              currentStep={currentStep}
              onSelectStep={this.handleSelectStep}
            />
          </>
        }
        body={
          <>
            <NtOnboardingHeader title={copy.title} subtitle={copy.subtitle} />
            {this.renderStep()}
          </>
        }
        footer={
          <NtOnboardingFooter
            steps={onboardingSteps}
            currentStep={currentStep}
            loading={loading}
            canContinue={this.isStepValid()}
            finishLabel={finishLabel}
            onBack={this.handleBack}
            onSkip={this.handleSkip}
            onContinue={this.handleContinue}
            onExit={this.handleExit}
          />
        }
      />
    );
  }
}

/**
 * Map the availability endpoint's `reason` enum to a friendly inline message.
 */
function aliasReasonMessage(reason) {
  switch (reason) {
    case "domain_taken":
    case "domain_taken_upstream":
      return "That alias is already taken. Try another one.";
    case "reserved":
      return "That alias is reserved. Please choose a different one.";
    case "invalid_format":
      return "That alias isn't a valid format.";
    default:
      return "That alias isn't available. Try another one.";
  }
}

/**
 * Decode the payload claims of a JWT. Mirrors the framework's internal helper
 * — duplicated here so we don't have to plumb a one-off export. Does NOT
 * verify the signature; we trust the BFF as the source of truth.
 */
function decodeJwtClaims(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return {};
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === "function"
      ? atob(padded)
      : Buffer.from(padded, "base64").toString("utf8");
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export default OnboardingController;
